/**
 * 頁面翻譯工具
 * 
 * 此工具用於協助翻譯應用程式中的所有頁面元素。
 * 導入並使用 translatePage 函數來翻譯 UI 元素。
 */

import languageManager from './index.js';
import LanguageSelector from '../components/LanguageSelector.js';

/**
 * 初始化頁面翻譯
 * @param {Object} options - 配置選項
 * @param {string} options.selectorContainerId - 語言選擇器容器的 ID（預設：'language-selector'）
 * @param {Object} options.elements - 元素選擇器到翻譯鍵的映射
 * @param {Function} options.customTranslations - 用於自訂翻譯的額外回調函數
 */
export function initPageTranslation(options = {}) {
    const {
        selectorContainerId = 'language-selector',
        elements = {},
        customTranslations = null
    } = options;

    // 初始化語言管理器
    languageManager.init();

    // 如果容器存在，初始化語言選擇器
    const selectorContainer = document.getElementById(selectorContainerId);
    if (selectorContainer) {
        const languageSelector = new LanguageSelector(selectorContainerId);
        languageSelector.init();
    }

    // 執行初始翻譯
    translatePage(elements, customTranslations);

    // 添加語言變更事件監聽器
    window.addEventListener('languageChanged', () => {
        translatePage(elements, customTranslations);
    });
}

/**
 * 翻譯頁面元素
 * @param {Object} elements - 元素選擇器到翻譯鍵的映射
 * @param {Function} customTranslations - 用於自訂翻譯的額外回調函數
 */
export function translatePage(elements = {}, customTranslations = null) {
    const t = languageManager.t;

    // 翻譯頁面標題
    if (elements.title) {
        document.title = typeof elements.title === 'function'
            ? elements.title(t)
            : t(elements.title);
    }

    // 翻譯文字元素
    Object.entries(elements).forEach(([selector, translationKey]) => {
        if (selector === 'title') return; // 跳過標題，已在上方處理

        const elements = document.querySelectorAll(selector);
        if (!elements.length) return;

        elements.forEach(element => {
            if (typeof translationKey === 'function') {
                // 如果翻譯鍵是函數，則使用元素和翻譯函數調用它
                translationKey(element, t);
            } else if (typeof translationKey === 'string') {
                // 直接翻譯
                element.textContent = t(translationKey);
            } else if (typeof translationKey === 'object') {
                // 處理屬性翻譯
                if (translationKey.text) {
                    element.textContent = t(translationKey.text);
                }
                if (translationKey.placeholder) {
                    element.placeholder = t(translationKey.placeholder);
                }
                if (translationKey.title) {
                    element.title = t(translationKey.title);
                }
                if (translationKey.value && element.tagName === 'INPUT') {
                    element.value = t(translationKey.value);
                }
                // 可根據需要添加更多屬性
            }
        });
    });

    // 如果提供了自訂翻譯函數，則執行它
    if (typeof customTranslations === 'function') {
        customTranslations(t);
    }
}

// 導出翻譯函數供直接使用
export const t = languageManager.t;

export default {
    init: initPageTranslation,
    translate: translatePage,
    t
}; 